/**
 * Flat "common rate" presets for the ordering settings form.
 *
 * Restaurant sales tax is one combined rate (state + county + city + special
 * districts), so the dashboard stores a single total in `locations.tax_rate_bps`.
 * These presets turn the well-known combined rates from `@adop/shared` into
 * one-click fills; anything else is typed into the exact-rate input. The stored
 * rate is always the single source of truth.
 */
import { US_STATE_TAXES } from '@adop/shared';
import { bpsToPercentLabel } from './tax';

/** Select value for "Custom / not listed". */
export const CUSTOM_TAX_PRESET = '';

export interface TaxPreset {
  /** Stable select value; `CUSTOM_TAX_PRESET` means "type your own rate". */
  code: string;
  /** e.g. "New York City, NY — 8.875%" or "Oregon — no sales tax". */
  label: string;
  /** Combined state + local rate for restaurant food, in bps. */
  rateBps: number;
}

const CUSTOM_PRESET: TaxPreset = {
  code: CUSTOM_TAX_PRESET,
  label: 'Custom / not listed',
  rateBps: 0,
};

/**
 * States with no state sales tax. Alaska is deliberately not here: it has no
 * state tax, but boroughs and cities may still tax meals, so a flat "no sales
 * tax" label would be misleading.
 */
const NO_SALES_TAX_STATE_CODES = ['DE', 'MT', 'NH', 'OR'] as const;

function buildPresets(): TaxPreset[] {
  const presets: TaxPreset[] = [];

  for (const state of US_STATE_TAXES) {
    for (const example of state.examples ?? []) {
      presets.push({
        code: `${state.code}:${example.label}`,
        label: `${example.label}, ${state.code} — ${bpsToPercentLabel(example.rateBps)}`,
        rateBps: example.rateBps,
      });
    }
  }

  for (const code of NO_SALES_TAX_STATE_CODES) {
    const state = US_STATE_TAXES.find((candidate) => candidate.code === code);
    if (!state) continue;
    presets.push({
      code: state.code,
      label: `${state.name} — no sales tax`,
      rateBps: 0,
    });
  }

  return presets.sort((a, b) => a.label.localeCompare(b.label));
}

const PRESETS: readonly TaxPreset[] = buildPresets();

/** Select options: "Custom / not listed" first, then the rates A–Z. */
export function presets(): TaxPreset[] {
  return [CUSTOM_PRESET, ...PRESETS];
}

/**
 * Preset code for an exact stored rate, or `CUSTOM_TAX_PRESET` when nothing
 * matches. `tax_rate_bps` is an integer, so half-basis-point presets (NYC's
 * 8.875%) are compared at the stored precision.
 *
 * 0 is the column default ("no tax configured") and four no-sales-tax presets
 * share it, so it is never mapped to a state — it stays Custom.
 */
export function presetForRate(rateBps: number | null | undefined): string {
  if (rateBps == null || rateBps === 0) return CUSTOM_TAX_PRESET;
  const rounded = Math.round(rateBps);
  const match = PRESETS.find((preset) => Math.round(preset.rateBps) === rounded);
  return match?.code ?? CUSTOM_TAX_PRESET;
}

/** Combined rate for a preset code; null for Custom or unknown codes. */
export function rateBpsForPreset(code: string): number | null {
  const preset = PRESETS.find((candidate) => candidate.code === code);
  return preset ? preset.rateBps : null;
}
